import type { Banner } from '../apps/hooks/banner/useBanner';

interface PresignedUrlResponse {
  presignedUrl: string;
  s3Key: string;
}

// Add interface to match server response
interface ListBannersResponse {
  banners: Banner[];
  lastEvaluatedKey?: string;
}

export class BannerClient {
  constructor(private readonly apiUrl: string) {}

  /**
   * Get a presigned URL for uploading to S3
   */
  async getPresignedUrl(fileName: string): Promise<PresignedUrlResponse> {
    const response = await fetch(`${this.getApiBaseUrl()}/banners/presignedUrl`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        fileName,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to get presigned URL: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Create a new banner
   */
  async createBanner(banner: Banner): Promise<Banner> {
    const response = await fetch(`${this.getApiBaseUrl()}/banners`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(banner),
    });

    if (!response.ok) {
      throw new Error(`Failed to create banner: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Create multiple banners with the same display period
   */
  async createBanners(banners: Banner[]): Promise<Banner[]> {
    const responses = await Promise.all(
      banners.map(async (banner) => {
        return await this.createBanner(banner);
      }),
    );

    return responses;
  }

  /**
   * Update an existing banner
   */
  async updateBanner(bannerId: string, banner: Partial<Banner>): Promise<Banner> {
    const response = await fetch(`${this.getApiBaseUrl()}/banners/${bannerId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(banner),
    });

    if (!response.ok) {
      throw new Error(`Failed to update banner: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Delete a banner
   */
  async deleteBanner(bannerId: string): Promise<void> {
    const response = await fetch(`${this.getApiBaseUrl()}/banners/${bannerId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      throw new Error(`Failed to delete banner: ${response.statusText}`);
    }
  }

  /**
   * Get a single banner by ID
   */
  async getBanner(bannerId: string): Promise<Banner> {
    const response = await fetch(`${this.getApiBaseUrl()}/banners/${bannerId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error(`Failed to get banner: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * List all banners with optional filtering
   */
  async listBanners(params?: {
    active?: boolean;
    lastEvaluatedKey?: string;
    limit?: number;
  }): Promise<ListBannersResponse> {
    let url = `${this.getApiBaseUrl()}/banners`;

    if (params) {
      const queryParams = new URLSearchParams();

      if (params.active !== undefined) {
        queryParams.append('active', params.active.toString());
      }

      if (params.lastEvaluatedKey) {
        queryParams.append('lastEvaluatedKey', params.lastEvaluatedKey);
      }

      if (params.limit) {
        queryParams.append('limit', params.limit.toString());
      }

      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    const response = await fetch(url, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error(`Failed to list banners: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Update banner orders in bulk
   */
  async updateBannerOrders(banners: Array<{ bannerId: string; order: number }>): Promise<void> {
    const response = await fetch(`${this.getApiBaseUrl()}/banners/bulk/order`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ banners }),
    });

    if (!response.ok) {
      throw new Error(`Failed to update banner orders: ${response.statusText}`);
    }
  }

  private getApiBaseUrl(): string {
    return this.apiUrl;
  }
}
